import { useIsPostsCommentsReactionsEnabled } from "@circle-react/hooks/useIsPostsCommentsReactionsEnabled";

import { t } from "@/i18n-js/instance";
import { useLikeComment } from "@/react/components/CommentsCompass/data/useLikeComment";
import { useLikesModal } from "@/react/components/CommentsCompass/Modals/LikesModal";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import { useCommentsDeepLink } from "@/react/components/CommentsCompass/Provider/useCommentsDeepLink";
import { useEngagementActionsVisibility } from "@/react/components/CommentsCompass/Thread/Comment/useEngagementActionsVisibility";
import { useCommentsThread } from "@/react/components/CommentsCompass/Thread/ThreadContext";
import { PostCommentReactionAction } from "@/react/components/Reactions/PostCommentReactionAction";
import { ReactionSummary } from "@/react/components/Reactions/PostCommentReactionAction/ReactionSummary";
import { cssAnchorProps } from "@/react/helpers/cssAnchorHelpers";
import { toLocaleString } from "@/react/helpers/number";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { commentLikeInvalidations } from "@/react/lib/queries/commentLikes";
import { commentCache } from "@/react/lib/queries/comments";
import type { Comment } from "@/react/types/Comment";
import { EMPTY_REACTION_COUNTS } from "@/react/types/ContentReaction";

import { ActionLink } from "./ActionLink";

interface EngagementActionsProps {
  comment: Comment;
}

export const EngagementActions = ({ comment }: EngagementActionsProps) => {
  const { isEmbedded, isReadOnly } = useCommentsConfig();
  const { canShowLikeAction, canShowReplyAction } =
    useEngagementActionsVisibility();
  const { toggleLike, isPending: isLikePending } = useLikeComment({ comment });
  const { openReplyForm } = useCommentsThread();
  const likesModal = useLikesModal();
  const { getCommentUrl } = useCommentsDeepLink();
  const isPostCommentReactionsEnabled = useIsPostsCommentsReactionsEnabled();

  const { is_liked: isLiked, likes_count: likesCount } = comment;
  const isLikesCountVisible = canShowLikeAction && likesCount > 0;

  const handleLikeCountClick = () => {
    if (isEmbedded) {
      window.open(getCommentUrl(comment.id), "_blank", "noreferrer");
      return;
    }

    void likesModal.show({ comment });
  };

  return (
    <div
      className={classNames(
        "flex min-h-8 items-center",
        isPostCommentReactionsEnabled ? "gap-3" : "gap-4",
      )}
      {...cssAnchorProps({ anchor: "comment-engagement-actions" })}
      data-testid="comment-engagement-actions"
      data-comment-id={comment.id}
    >
      {isPostCommentReactionsEnabled && canShowLikeAction && (
        <PostCommentReactionAction
          canReact={!isReadOnly}
          currentUserReaction={
            comment.reaction_summary?.current_user_reaction_type
          }
          iconOnly
          isLiked={isLiked}
          likesCount={likesCount}
          loginNotice={t("comments.log_in_to_like_comments")}
          onChange={updates => {
            commentCache.update({
              postId: comment.post_id,
              id: comment.id,
              updater: current => ({ ...current, ...updates }),
            });
          }}
          onSettled={() => commentLikeInvalidations.list(comment.id)}
          reactionCounts={
            comment.reaction_summary?.counts ?? EMPTY_REACTION_COUNTS
          }
          reactionableId={comment.id}
          reactionableType="comment"
          showSummary={false}
        />
      )}
      {!isPostCommentReactionsEnabled && canShowLikeAction && (
        <ActionLink
          requiresAuth
          active={isLiked}
          anchorProps={cssAnchorProps({
            anchor: "comment-like-button",
            modifiers: { liked: isLiked },
          })}
          disabled={isLikePending || isReadOnly}
          onClick={toggleLike}
        >
          {isLiked
            ? t("comments.engagement_actions.liked")
            : t("comments.engagement_actions.like")}
        </ActionLink>
      )}
      {canShowReplyAction && (
        <ActionLink
          requiresAuth
          anchorProps={cssAnchorProps({ anchor: "comment-reply-button" })}
          disabled={isReadOnly}
          onClick={() =>
            openReplyForm({ communityMember: comment.community_member })
          }
        >
          {t("comments.engagement_actions.reply")}
        </ActionLink>
      )}
      {isLikesCountVisible && isPostCommentReactionsEnabled && (
        <ReactionSummary
          className="ml-auto"
          count={likesCount}
          data-testid="comment-reaction-summary"
          counts={comment.reaction_summary?.counts ?? EMPTY_REACTION_COUNTS}
          onClick={handleLikeCountClick}
        />
      )}
      {isLikesCountVisible && !isPostCommentReactionsEnabled && (
        <ActionLink
          className="ml-auto"
          anchorProps={cssAnchorProps({ anchor: "comment-like-count" })}
          dataTestId="like-count"
          onClick={handleLikeCountClick}
        >
          {t("comments.likes", {
            count: likesCount,
            formatted_count: toLocaleString(likesCount),
          })}
        </ActionLink>
      )}
    </div>
  );
};
